import React, { useState, useEffect } from "react";
import LandingHeader from "../components/layout/LandingHeader";
import LandingFooter from "../components/layout/LandingFooter";
import {
  Info,
  UserPlus,
  Settings,
  Share2,
  Shield,
  CheckCircle2,
  Eye,
  Mail,
  Lock,
  Ban,
  Sliders,
  MessageSquare,
} from "lucide-react";

const sections = [
  { id: "introducao", title: "1. Introdução", icon: Info },
  { id: "dados-coletados", title: "2. Dados Coletados", icon: UserPlus },
  { id: "uso-dados", title: "3. Uso dos Dados", icon: Settings },
  { id: "compartilhamento", title: "4. Compartilhamento", icon: Share2 },
  { id: "seguranca", title: "5. Segurança dos Dados", icon: Shield },
  { id: "direitos-lgpd", title: "6. Seus Direitos (LGPD)", icon: CheckCircle2 },
  { id: "cookies", title: "7. Cookies e Rastreamento", icon: Eye },
  { id: "contato", title: "8. Canal de Contato", icon: Mail },
];

function PoliticaPrivacidade() {
  const [activeSection, setActiveSection] = useState("introducao");

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y =
        element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveSection(id);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 120;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const top = element.offsetTop;
          const height = element.offsetHeight;

          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-urb-dark text-slate-100 selection:bg-urb-primary selection:text-black">
      <LandingHeader />

      <main className="flex-1 py-10 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col gap-8">
          {/* Header Banner */}
          <div className="flex flex-col gap-2 mb-2 text-center md:text-left">
            <span className="font-semibold text-xs sm:text-sm tracking-wider uppercase text-urb-primary">
              URB RACE &bull; SEGURANÇA E TRANSPARÊNCIA
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black m-0 leading-tight">
              POLÍTICA DE{" "}
              <span className="text-urb-primary drop-shadow-[0_0_12px_rgba(138,230,0,0.3)]">
                PRIVACIDADE
              </span>
            </h1>
            <p className="text-slate-400 text-sm sm:text-base m-0 max-w-2xl leading-relaxed">
              Esta política explica como coletamos, usamos, armazenamos e
              protegemos seus dados pessoais. Valorizamos sua privacidade e
              garantimos total transparência na sua jornada esportiva.
            </p>
            <span className="text-xs text-slate-500 font-medium mt-1">
              Última atualização: 22 de junho de 2026
            </span>
          </div>

          {/* Quick Highlight Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 rounded-2xl border border-urb-primary/20 bg-urb-primary/5 flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-urb-primary" />
                <h4 className="m-0 text-white font-bold text-sm">
                  Proteção Total
                </h4>
              </div>
              <p className="m-0 text-xs text-slate-400 leading-relaxed">
                Seus dados bancários e de cartões não ficam salvos no nosso
                servidor. Todo o pagamento é processado via parceiros de
                segurança certificada.
              </p>
            </div>
            <div className="p-5 rounded-2xl border border-urb-primary/20 bg-urb-primary/5 flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <Ban className="w-5 h-5 text-urb-primary" />
                <h4 className="m-0 text-white font-bold text-sm">
                  Não vendemos seus dados
                </h4>
              </div>
              <p className="m-0 text-xs text-slate-400 leading-relaxed">
                Nós não compartilhamos ou vendemos suas informações pessoais
                para empresas terceiras para fins publicitários ou promocionais.
              </p>
            </div>
            <div className="p-5 rounded-2xl border border-urb-primary/20 bg-urb-primary/5 flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <Sliders className="w-5 h-5 text-urb-primary" />
                <h4 className="m-0 text-white font-bold text-sm">
                  Controle do Usuário
                </h4>
              </div>
              <p className="m-0 text-xs text-slate-400 leading-relaxed">
                Você possui controle total sobre suas informações, podendo
                corrigir, exportar ou solicitar a exclusão de seus dados
                diretamente pelo canal de suporte.
              </p>
            </div>
          </div>

          {/* Main Layout Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left Side: Sticky Navigation (Desktop) */}
            <div className="lg:col-span-3 hidden lg:flex flex-col gap-1.5 sticky top-24 pt-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                TÓPICOS DA POLÍTICA
              </span>
              <div className="flex flex-col border-l border-slate-800">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={`text-left py-2 px-3 bg-transparent cursor-pointer font-semibold text-xs transition-all flex items-center gap-2 -ml-[1px] border-l-2 ${
                        isActive
                          ? "text-urb-primary border-urb-primary bg-urb-primary/5"
                          : "text-slate-400 border-transparent hover:text-white"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      {section.title}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mobile Horizontal Section Selector */}
            <div className="lg:hidden flex overflow-x-auto gap-2 py-2 mb-2 w-full border-b border-urb-border">
              {sections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    className={`py-1.5 px-3 rounded-lg font-semibold text-xs whitespace-nowrap cursor-pointer flex items-center gap-1.5 border transition-all ${
                      isActive
                        ? "bg-urb-primary border-urb-primary text-black font-bold"
                        : "bg-slate-900 border-slate-800 text-slate-300"
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    {section.title.split(". ")[1]}
                  </button>
                );
              })}
            </div>

            {/* Right Side: Detailed Privacy Policy Content */}
            <div className="lg:col-span-9 flex flex-col gap-6 text-slate-300 text-xs sm:text-sm leading-relaxed">
              {/* Section 1: Introdução */}
              <section
                id="introducao"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <Info className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    1. Introdução
                  </h3>
                </div>
                <p>
                  A <strong>URB RACE</strong> se compromete com a privacidade,
                  confidencialidade e segurança dos dados pessoais de seus
                  atletas, organizadores, parceiros e visitantes. Esta Política
                  de Privacidade descreve detalhadamente como suas informações
                  são tratadas de forma íntegra e segura.
                </p>
                <p>
                  Ao utilizar nosso site, aplicativos ou serviços relacionados ao
                  ecossistema da URB RACE, você compreende e consente com a
                  coleta, uso e arquivamento dos dados que forem necessários, nas
                  formas e limites aqui indicados, em estrita observância à Lei
                  Geral de Proteção de Dados (Lei nº 13.709/2018 - LGPD).
                </p>
              </section>

              {/* Section 2: Dados Coletados */}
              <section
                id="dados-coletados"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <UserPlus className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    2. Dados Coletados
                  </h3>
                </div>
                <p>
                  Para fornecer nossos serviços de inscrições e gerenciamento de
                  corridas de rua de forma eficiente, coletamos diferentes tipos
                  de informações:
                </p>
                <h4 className="text-white font-bold m-0 mt-2 text-sm">
                  A) Para Atletas (Participantes):
                </h4>
                <ul className="m-0 pl-5 flex flex-col gap-1.5 list-disc">
                  <li>
                    <strong>Informações Cadastrais:</strong> Nome completo, CPF,
                    data de nascimento, gênero, tamanho de camiseta (para
                    confecção do kit da prova).
                  </li>
                  <li>
                    <strong>Informações de Contato:</strong> E-mail, telefone
                    celular / WhatsApp.
                  </li>
                  <li>
                    <strong>Informações de Segurança:</strong> Contato de
                    emergência (nome e telefone de um terceiro indicado pelo
                    participante).
                  </li>
                  <li>
                    <strong>Informações de Pagamento:</strong> Processadas
                    diretamente pelo nosso intermediador parceiro (Mercado
                    Pago). A URB RACE não retém nem visualiza o número do seu
                    cartão de crédito ou código CVV em seus servidores.
                  </li>
                </ul>

                <h4 className="text-white font-bold m-0 mt-2 text-sm">
                  B) Para Organizadores de Eventos:
                </h4>
                <ul className="m-0 pl-5 flex flex-col gap-1.5 list-disc">
                  <li>
                    <strong>Informações do Negócio:</strong> Nome da
                    empresa/organizador, CNPJ ou CPF do responsável legal,
                    endereço comercial, e-mail institucional e telefone.
                  </li>
                  <li>
                    <strong>Dados Financeiros:</strong> Dados de conta bancária
                    de titularidade do organizador, necessários exclusivamente
                    para efetuar o repasse dos valores arrecadados nas
                    inscrições do respectivo evento.
                  </li>
                </ul>
              </section>

              {/* Section 3: Uso dos Dados */}
              <section
                id="uso-dados"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <Settings className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    3. Finalidade do Tratamento dos Dados
                  </h3>
                </div>
                <p>
                  Utilizamos as informações coletadas com as seguintes
                  finalidades legítimas:
                </p>
                <ul className="m-0 pl-5 flex flex-col gap-1.5 list-disc">
                  <li>
                    <strong>Execução de Contrato:</strong> Garantir sua correta
                    inscrição nas corridas escolhidas, classificar sua categoria
                    de faixa etária na cronometragem e entregar o tamanho correto
                    de kit.
                  </li>
                  <li>
                    <strong>Comunicação Transacional:</strong> Enviar e-mails de
                    confirmação de inscrição, recibo de pagamento, lembretes de
                    retirada de kit e alteração no cronograma do evento.
                  </li>
                  <li>
                    <strong>Segurança Física:</strong> Fornecer assistência
                    médica primária durante as provas (utilizando a ficha de
                    alergias e contatos de emergência fornecidos pelo
                    participante).
                  </li>
                  <li>
                    <strong>Obrigações Legais:</strong> Cumprir com as obrigações
                    fiscais, emissão de notas fiscais e atendimento a requisições
                    judiciais.
                  </li>
                </ul>
              </section>

              {/* Section 4: Compartilhamento */}
              <section
                id="compartilhamento"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <Share2 className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    4. Compartilhamento de Dados
                  </h3>
                </div>
                <p>
                  O compartilhamento de dados ocorre de forma limitada e
                  estritamente necessária para viabilizar as corridas:
                </p>
                <ul className="m-0 pl-5 flex flex-col gap-1.5 list-disc">
                  <li>
                    <strong>Com os Organizadores do Evento:</strong>{" "}
                    Compartilhamos a lista de atletas inscritos em suas
                    respectivas provas, contendo nome, CPF (para entrega de kit e
                    checagem de documento), gênero, idade e tamanho de camiseta.
                    Os organizadores ficam vinculados a esta mesma política de
                    privacidade.
                  </li>
                  <li>
                    <strong>Com Provedores de Serviço:</strong> Empresas
                    terceiras contratadas para serviços de cronometragem
                    eletrônica (chip de peito), que necessitam do nome e gênero
                    dos atletas para divulgar os resultados.
                  </li>
                  <li>
                    <strong>Gateway de Pagamento:</strong> Envio de informações
                    de transação ao Mercado Pago para processar as operações
                    financeiras.
                  </li>
                </ul>
                <p className="m-0 mt-2 font-bold text-white">
                  A URB RACE nunca comercializa nem transfere seus dados pessoais
                  para terceiros para fins de marketing.
                </p>
              </section>

              {/* Section 5: Segurança */}
              <section
                id="seguranca"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <Shield className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    5. Segurança dos Dados
                  </h3>
                </div>
                <p>
                  Implementamos medidas físicas, técnicas e organizacionais de
                  segurança para proteger seus dados contra perda, furto, acesso
                  não autorizado, divulgação ou alteração indevida.
                </p>
                <p>
                  Todas as conexões com nossa plataforma web são criptografadas
                  utilizando tecnologia <strong>HTTPS/SSL</strong>. O banco de
                  dados opera em servidores com regras restritas de acesso e
                  monitoramento contínuo.
                </p>
              </section>

              {/* Section 6: Direitos LGPD */}
              <section
                id="direitos-lgpd"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <CheckCircle2 className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    6. Seus Direitos (LGPD)
                  </h3>
                </div>
                <p>
                  Em conformidade com o artigo 18 da Lei Geral de Proteção de
                  Dados (LGPD), você possui os seguintes direitos:
                </p>
                <ul className="m-0 pl-5 flex flex-col gap-1.5 list-disc">
                  <li>
                    <strong>Acesso e Confirmação:</strong> Saber se tratamos seus
                    dados e quais dados possuímos de você.
                  </li>
                  <li>
                    <strong>Correção:</strong> Solicitar a alteração de dados
                    incompletos, inexatos ou desatualizados.
                  </li>
                  <li>
                    <strong>Portabilidade:</strong> Solicitar a transferência
                    dos seus dados a outro prestador de serviço.
                  </li>
                  <li>
                    <strong>Eliminação:</strong> Requerer a exclusão definitiva
                    dos seus dados (ressalvada a retenção legal necessária).
                  </li>
                </ul>
              </section>

              {/* Section 7: Cookies */}
              <section
                id="cookies"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <Eye className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    7. Cookies e Rastreamento
                  </h3>
                </div>
                <p>
                  A URB RACE utiliza cookies estritamente necessários e
                  analíticos para manter você logado com segurança e melhorar o
                  desempenho da aplicação.
                </p>
              </section>

              {/* Section 8: Contato */}
              <section
                id="contato"
                className="p-6 rounded-2xl border border-urb-border bg-urb-card/70 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 border-b border-urb-border pb-3">
                  <Mail className="w-5 h-5 text-urb-primary" />
                  <h3 className="m-0 text-white font-bold text-lg">
                    8. Canal de Contato
                  </h3>
                </div>
                <p>
                  Se você tiver dúvidas sobre a proteção de dados ou desejar
                  exercer seus direitos, entre em contato com nosso time:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                  <div className="flex items-center gap-3 p-4 rounded-xl border border-urb-border bg-slate-950/60">
                    <Mail className="w-5 h-5 text-urb-primary" />
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-400">
                        E-mail de Contato
                      </span>
                      <span className="font-semibold text-white text-xs sm:text-sm">
                        privacidade@urbrace.com.br
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-xl border border-urb-border bg-slate-950/60">
                    <MessageSquare className="w-5 h-5 text-urb-primary" />
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-400">
                        Tempo Médio de Resposta
                      </span>
                      <span className="font-semibold text-white text-xs sm:text-sm">
                        Até 48 horas úteis
                      </span>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}

export default PoliticaPrivacidade;
